/**
 * FactoryDeductionRatePage — `/factory/<code>/deduction-rate`.
 *
 * Bám Access `frmFactoryDeductionRealSubForm` + Subform
 * `frmFactoryQryDeductionReal_baoloc`:
 *   List = PIVOT TABLE từ `view_factory_deduction_real_quality`.
 *   Mỗi row = 1 (parameter, field_value); cột quality động theo
 *   `public.list_and_valo WHERE is_deduction_show=1` ORDER BY ranked, code DESC.
 *   Cell = `quality_rate` (deduction rate %).
 *
 * Buttons:
 *   - btnNew (line 864): open dialog với field_value editable, template
 *     từ master quality_codes (value=0, rate=0).
 *   - btnEdit (line 819): open dialog cho (parameter, field_value) hiện
 *     tại; field_value LOCKED (Access line 851); bulk upsert.
 *   - btnDelete (line 789): soft-delete rows cho (factory, parameter,
 *     field_value).
 *   - Permission: `screenAccess <> 1` ⇒ disabled.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PercentIcon from '@mui/icons-material/Percent'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useMemo, useState, type CSSProperties } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useIsMobile } from '@/hooks/useResponsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import {
  useDeleteDeductionRateMutation,
  useGetDeductionRateListQuery,
  useLazyGetDeductionRateDetailQuery,
  useSaveDeductionRateMutation,
  type FactoryCell,
} from '@/store/api/factoryApi'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '@/theme/responsive'

import DeductionRateFormDialog, {
  type DeductionRateBulkRow,
} from './DeductionRateFormDialog'

const DR_ACCENT = '#8f5a2e'

type PivotRow = Record<string, FactoryCell>

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

interface ColumnDef {
  key: string
  label: string
  kind?: 'number' | 'datetime'
  align?: 'right'
  filter?: ColumnFilterKind
}

const META_LEFT_COLUMNS: ColumnDef[] = [
  { key: 'factory_code', label: 'Factory' },
  { key: 'parameter_code', label: 'Parameter' },
  { key: 'field_name', label: 'Field Name' },
  { key: 'field_value', label: 'Field Value', kind: 'number', align: 'right' },
  { key: 'mc_percent', label: 'MC %', kind: 'number', align: 'right' },
]
const META_RIGHT_COLUMNS: ColumnDef[] = [
  { key: 'updated_date', label: 'Updated Date', kind: 'datetime' },
  { key: 'updated_by', label: 'Updated By' },
]

/** Cột freeze khi scroll ngang — chỉ cột định danh row. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['field_name'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { field_name: 180 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { field_name: 160 }

const formatNumberCell = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

const formatDateTime = (value: unknown): string => {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const cellValue = (row: PivotRow, col: ColumnDef): string => {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'number') return formatNumberCell(v)
  if (col.kind === 'datetime') return formatDateTime(v)
  return String(v)
}

const distinctValues = (rows: PivotRow[], key: string): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const v = row[key]
    if (v == null) continue
    const t = String(v).trim()
    if (t) values.add(t)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowKey = (row: PivotRow): string =>
  `${String(row.parameter_code ?? '')}||${String(row.field_value ?? '')}`

interface FactoryDeductionRatePageProps {
  factoryCode: string
  onBackToHub: () => void
  canEdit: boolean
}

type DialogState =
  | { kind: 'closed' }
  | {
      kind: 'edit' | 'new'
      parameterCode: string
      parameterName: string
      fieldName: string
      fieldValue: number
      fieldValueLocked: boolean
      mcPercent: number | null
      rows: DeductionRateBulkRow[]
      loading: boolean
    }

interface PendingDelete {
  parameter_code: string
  parameter_name: string
  field_value: number
}

const ALL_ROWS_PAGE_SIZE = -1

export default function FactoryDeductionRatePage({
  factoryCode,
  onBackToHub,
  canEdit,
}: FactoryDeductionRatePageProps) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const list = useGetDeductionRateListQuery({ factoryCode })
  const [triggerDetail, { isFetching: detailFetching }] =
    useLazyGetDeductionRateDetailQuery()
  const [saveBulk, { isLoading: saving }] = useSaveDeductionRateMutation()
  const [deleteByKey, { isLoading: deleting }] = useDeleteDeductionRateMutation()

  const {
    selectedId: selectedKey,
    setSelectedId: setSelectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const { getWidth, startResize } = useColumnWidths('factory.deductionRate.pivot')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const allColumns = useMemo<ColumnDef[]>(() => {
    const qualityCols: ColumnDef[] = (list.data?.quality_columns ?? []).map((q) => ({
      key: q.quality_factory,
      label: q.code,
      kind: 'number',
      align: 'right',
    }))
    return [...META_LEFT_COLUMNS, ...qualityCols, ...META_RIGHT_COLUMNS]
  }, [list.data?.quality_columns])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'factory',
    prefKey: 'columns',
    subKey: 'deduction-rate',
    allColumns,
    widthsTableKey: 'factory.deductionRate.pivot',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo<PivotRow[]>(() => list.data?.items ?? [], [list.data])

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm, allColumns)) return false
        if (!rowMatchesAllColumnFilters(row, columnFilters.filters)) return false
        return true
      }),
    [baseRows, searchTerm, allColumns, columnFilters.filters],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? allColumns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, allColumns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, allColumns),
    [filteredRows, sortState, allColumns],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  // Quality columns ~80; với 110px/col bảng quá rộng (>9000px). Giảm
   // per-column factor xuống 72 → bảng default ~6000-6500px, vẫn đọc được
   // số 4-chữ-số. User vẫn resize được column riêng qua ResizeHandle.
  const minTableWidth = Math.max(720, visibleColumns.length * 72)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo(
    () => baseRows.find((r) => rowKey(r) === selectedKey) ?? null,
    [baseRows, selectedKey],
  )

  const openEditDialog = async (row: PivotRow) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    const pcode = String(row.parameter_code ?? '')
    const fv = Number(row.field_value ?? 0)
    setDialog({
      kind: 'edit',
      parameterCode: pcode,
      parameterName: String(row.parameter_name ?? ''),
      fieldName: String(row.field_name ?? ''),
      fieldValue: fv,
      fieldValueLocked: true,
      mcPercent: row.mc_percent != null ? Number(row.mc_percent) : null,
      rows: [],
      loading: true,
    })
    try {
      const detail = await triggerDetail({
        factoryCode,
        parameterCode: pcode,
        fieldValue: fv,
        isNew: false,
      }).unwrap()
      setDialog((prev) =>
        prev.kind === 'edit' && prev.parameterCode === pcode && prev.fieldValue === fv
          ? {
              ...prev,
              parameterName: detail.parameter_name ?? prev.parameterName,
              fieldName: detail.field_name ?? prev.fieldName,
              mcPercent: detail.mc_percent,
              rows: detail.rows.map((r) => ({
                quality_code: r.quality_code,
                quality_value: r.quality_value,
                quality_rate: r.quality_rate,
              })),
              loading: false,
            }
          : prev,
      )
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Could not load detail')
          : 'Could not load detail'
      snack.error(msg)
      setDialog({ kind: 'closed' })
    }
  }

  const openNewDialog = async (row: PivotRow) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    const pcode = String(row.parameter_code ?? '')
    setDialog({
      kind: 'new',
      parameterCode: pcode,
      parameterName: String(row.parameter_name ?? ''),
      fieldName: String(row.field_name ?? ''),
      fieldValue: 0,
      fieldValueLocked: false,
      mcPercent: 0,
      rows: [],
      loading: true,
    })
    try {
      const detail = await triggerDetail({
        factoryCode,
        parameterCode: pcode,
        fieldValue: 0,
        isNew: true,
      }).unwrap()
      setDialog((prev) =>
        prev.kind === 'new' && prev.parameterCode === pcode
          ? {
              ...prev,
              parameterName: detail.parameter_name ?? prev.parameterName,
              fieldName: detail.field_name ?? prev.fieldName,
              rows: detail.rows.map((r) => ({
                quality_code: r.quality_code,
                quality_value: 0,
                quality_rate: 0,
              })),
              loading: false,
            }
          : prev,
      )
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Could not load template')
          : 'Could not load template'
      snack.error(msg)
      setDialog({ kind: 'closed' })
    }
  }

  const handleNewClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    void openNewDialog(selectedRow)
  }

  const handleEditClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    void openEditDialog(selectedRow)
  }

  const handleDeleteClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPendingDelete({
      parameter_code: String(selectedRow.parameter_code ?? ''),
      parameter_name: String(selectedRow.parameter_name ?? ''),
      field_value: Number(selectedRow.field_value ?? 0),
    })
  }

  const handleSave = async (payload: {
    fieldValue: number
    mcPercent: number | null
    rows: DeductionRateBulkRow[]
  }) => {
    if (!canEdit) return
    if (dialog.kind !== 'edit' && dialog.kind !== 'new') return
    try {
      await saveBulk({
        factoryCode,
        parameterCode: dialog.parameterCode,
        fieldValue: payload.fieldValue,
        body: {
          mc_percent: payload.mcPercent,
          rows: payload.rows.map((r) => ({
            quality_code: r.quality_code,
            quality_value: r.quality_value,
            quality_rate: r.quality_rate,
          })),
        },
      }).unwrap()
      snack.success(dialog.kind === 'new' ? 'Deduction rate added' : 'Deduction rate saved')
      setDialog({ kind: 'closed' })
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Save failed')
          : 'Save failed'
      snack.error(msg)
    }
  }

  const handleConfirmDelete = async () => {
    if (!canEdit || !pendingDelete) return
    try {
      await deleteByKey({
        factoryCode,
        parameterCode: pendingDelete.parameter_code,
        fieldValue: pendingDelete.field_value,
      }).unwrap()
      snack.success('Deduction rate deleted')
      if (selectedKey === `${pendingDelete.parameter_code}||${pendingDelete.field_value}`) {
        setSelectedKey(null)
      }
      setPendingDelete(null)
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Delete failed')
          : 'Delete failed'
      snack.error(msg)
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={onBackToHub}
            sx={filterBarBtnSx}
          >
            Datasets
          </Button>
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              mx: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: DR_ACCENT,
              color: '#fff',
              boxShadow: `0 1px 3px ${alpha(DR_ACCENT, 0.45)}`,
            }}
          >
            <PercentIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              DEDUCTION RATE
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Tooltip title={canEdit ? 'Add new field value for selected parameter' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="contained"
                size="small"
                color="success"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || detailFetching || saving}
                onClick={handleNewClick}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                title="New"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit rates for selected entry' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || detailFetching || saving}
                onClick={handleEditClick}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                title="Edit"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Delete selected entry' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || deleting}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {list.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load deduction rate list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(list.isLoading || list.isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Parameter, field, value..."
              label="Search Deduction Rate"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
          {list.isError ? (
            <BrandState
              tone="error"
              title="Could not load deduction rate list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : list.isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No deduction rate records found"
              description="Try adjusting the search or field filter above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) {
                      headerStyle.width = w
                      headerStyle.minWidth = w
                      headerStyle.maxWidth = w
                    }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'none',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: PivotRow) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const k = row ? rowKey(row) : null
                  const isSelected = k != null && k === selectedKey
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (k != null) toggleSelectKey(k)
                      }}
                      onDoubleClick={() => {
                        if (!canEdit || row == null) return
                        void openEditDialog(row)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <DeductionRateFormDialog
        open={dialog.kind !== 'closed'}
        mode={dialog.kind === 'new' ? 'new' : 'edit'}
        loading={(dialog.kind === 'edit' || dialog.kind === 'new') && dialog.loading}
        saving={saving}
        canEdit={canEdit}
        factoryCode={factoryCode}
        parameterCode={dialog.kind !== 'closed' ? dialog.parameterCode : ''}
        parameterName={dialog.kind !== 'closed' ? dialog.parameterName : ''}
        fieldName={dialog.kind !== 'closed' ? dialog.fieldName : ''}
        fieldValue={dialog.kind !== 'closed' ? dialog.fieldValue : 0}
        fieldValueLocked={dialog.kind !== 'closed' ? dialog.fieldValueLocked : true}
        mcPercent={dialog.kind !== 'closed' ? dialog.mcPercent : null}
        rows={dialog.kind !== 'closed' ? dialog.rows : []}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete Deduction Rate"
        message={
          pendingDelete
            ? `Delete rates for "${pendingDelete.parameter_code}"${pendingDelete.parameter_name ? ` (${pendingDelete.parameter_name})` : ''} @ value ${pendingDelete.field_value}?`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
